/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Locale } from "../../locale/Locale";
import { LocaleKeys } from "../../locale/LocaleKeys";
import { Log, Diagnostics } from "../../logging";
import type { TPlayerError } from "../types";
import { PlaybackError } from "./PlaybackError";
import { PLAYER_ERROR_SEPARATOR } from "./PlayerErrorCodes";
import { getWebErrorCodeDescriptions } from "./PlayerErrorMessages";

/**
 * Maps a normalized {@link TPlayerError} (already carrying a compound V9xx
 * error code, e.g. `V951-001`) into a user-facing {@link PlaybackError} with a
 * localized title and description.
 *
 * Mirrors kaveri-web-app's `LiveTvPlaybackErrorMapper.setPlaybackErrorPropertiesByCode`,
 * adapted to unity-web's `PlaybackError`. Pure with respect to Shaka — it only
 * reads the V9xx prefix of {@link TPlayerError.code}, so it needs no
 * `shaka-player` dependency.
 */
export class PlaybackErrorMapper {
  private static readonly TAG: string = "PlaybackErrorMapper";

  /**
   * Build a localized {@link PlaybackError} from a normalized player error.
   *
   * @param error - The normalized player error (compound `Vxxx-NNN` code).
   * @returns A {@link PlaybackError} with localized title and description.
   */
  public static fromPlayerError(error: TPlayerError): PlaybackError {
    Log.e(PlaybackErrorMapper.TAG, "playback error code ", error.code);
    Diagnostics.error(
      PlaybackErrorMapper.TAG,
      "playback error code ",
      error.code
    );
    const baseCode = error.code.split(PLAYER_ERROR_SEPARATOR)[0];
    const catalogEntry = getWebErrorCodeDescriptions().get(baseCode);

    const title: string = (catalogEntry && catalogEntry.title) || "";

    const description =
      catalogEntry !== undefined
        ? catalogEntry.description
        : Locale.getString(LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE);

    return new PlaybackError(error.code, error.message, title, description);
  }
}
